/**
 * ABA routing number validation.
 *
 * A routing number is nine digits whose weighted sum is divisible by ten:
 *
 *     3(d1 + d4 + d7) + 7(d2 + d5 + d8) + 1(d3 + d6 + d9)  ≡ 0 (mod 10)
 *
 * This catches every single-digit typo and most transpositions, which is the
 * overwhelming majority of what a customer gets wrong when reading a number off
 * a check. It does NOT prove the institution exists or that the account is
 * open — only the ODFI can tell you that. Treat a pass here as "worth
 * submitting", never as "verified".
 */
const WEIGHTS = [3, 7, 1, 3, 7, 1, 3, 7, 1];
/** Strips spaces and dashes; returns digits only. */
export function normalizeRoutingNumber(input) {
    return input.replace(/[\s-]/g, '');
}
export function isValidRoutingNumber(input) {
    const digits = normalizeRoutingNumber(input);
    if (!/^\d{9}$/.test(digits))
        return false;
    // All-zero passes the checksum arithmetic but is not a real institution, and
    // it is what an empty auto-filled field turns into.
    if (digits === '000000000')
        return false;
    let sum = 0;
    for (let i = 0; i < 9; i += 1) {
        sum += Number(digits[i]) * WEIGHTS[i];
    }
    return sum % 10 === 0;
}
/**
 * The first two digits are the Federal Reserve district. 00–12 are the
 * districts themselves, 21–32 are thrift institutions, 61–72 are electronic
 * transaction identifiers, and 80 is a traveler's check. Anything else was
 * never issued.
 */
export function hasPlausibleRoutingPrefix(input) {
    const digits = normalizeRoutingNumber(input);
    if (!/^\d{9}$/.test(digits))
        return false;
    const prefix = Number(digits.slice(0, 2));
    return ((prefix >= 0 && prefix <= 12) ||
        (prefix >= 21 && prefix <= 32) ||
        (prefix >= 61 && prefix <= 72) ||
        prefix === 80);
}
/**
 * Account numbers vary far more than routing numbers — 4 to 17 digits
 * depending on the institution, sometimes with leading zeros that must be
 * preserved. So this is a shape check, not a checksum: there is no arithmetic
 * that can validate one.
 */
export function normalizeAccountNumber(input) {
    return input.replace(/[\s-]/g, '');
}
export function isPlausibleAccountNumber(input) {
    const digits = normalizeAccountNumber(input);
    if (!/^\d{4,17}$/.test(digits))
        return false;
    if (/^0+$/.test(digits))
        return false;
    return true;
}
/** Last four digits, for display. Short numbers are still only ever shown as four. */
export function last4(input) {
    const digits = input.replace(/\D/g, '');
    return digits.slice(-4).padStart(4, '•');
}
//# sourceMappingURL=routing-number.js.map